/** Bounded retry for the one-shot remote calls a database restore makes.
 *
 * WHY THIS EXISTS. `DatabaseWrapper.restoreFromDocsAsync` replaces the remote database using plain
 * `allDocs` / `bulkDocs` calls. Those are NOT replication, so PouchDB's `retry: true` does not cover
 * them — nothing re-drives them, and one dropped request rejects the whole restore. That is not
 * hypothetical: a CI burst server changed its network interface mid-restore
 * (`net::ERR_NETWORK_CHANGED`), the delete `bulkDocs` rejected with "Failed to fetch" 53ms in, and the
 * restore died there — which took out an entire nightly E2E section.
 *
 * A transient blip should cost a retry, not the restore. Anything else is a real fault and is rethrown
 * unchanged, so the retry can never mask one.
 *
 * Kept as a pure function — the caller injects the clock and the transient-error predicate — so the
 * attempt bound, the backoff, and the rethrow-vs-retry decision can be unit-tested directly instead of
 * only being asserted on by a source tripwire. See `restore-retry.test.ts`.
 */

/** How many times a remote restore call may be attempted in total (the first try plus 4 retries).
 * Bounded on purpose: a network that is genuinely down must fail the restore, not hang it. */
export const RESTORE_REMOTE_MAX_ATTEMPTS = 5;

/** How many times the local database's `destroy()` may be attempted after the remote has been restored.
 * It gets its own (higher) bound because the cost of giving up is much worse than a slow retry: a local
 * database that survives still holds the PRE-restore documents, and if anything re-attaches sync to it,
 * those older revisions overwrite the restore we just completed. */
export const RESTORE_LOCAL_DESTROY_MAX_ATTEMPTS = 8;

/** Thrown when a restore fails AFTER the remote already holds the restored data — i.e. while closing
 * the remote handle or destroying the now-stale local database.
 *
 * This is not a failed restore, and a caller must not treat it as one. The normal failure recovery is
 * "reload, and the still-intact local database syncs its docs back up", which repairs a restore that
 * died mid-write. Run that same recovery here and it does the opposite: it pushes the OLD docs over
 * the restore that just succeeded. So the caller reports this case differently and must not promise
 * the user their previous data back. */
export class RestoreAfterRemoteReplacedError extends Error {
  constructor(public readonly cause: unknown) {
    super(`[restore] the data was restored, but cleaning up afterwards failed: ${(cause as any)?.message ?? cause}`);
    this.name = 'RestoreAfterRemoteReplacedError';
  }
}

export interface RetryOnTransientErrorOptions {
  /** human-readable name of the call, used only in the log lines */
  operationLabel: string;
  maxAttempts: number;
  /** true when the error is a transient network blip and the call is worth repeating */
  isTransient: (err: unknown) => boolean;
  sleepAsync: (ms: number) => Promise<void>;
  /** Called before each repeat. The caller needs to know a repeat happened, because a retried write may
   * have ALREADY been applied by the server — see the note on `assertBulkDocsChunkSucceeded`. */
  onRetry?: () => void;
  /** Tag put at the front of this helper's log lines, so a reader can tell WHICH operation retried.
   *
   * Defaults to `[restore]` because that was the only caller when this was written, and the default keeps
   * those log lines byte-identical. Other callers pass their own — the purge passes `[purge]` — because a
   * line reading `[restore] ...` in the middle of a delete-my-data flow sends the next person reading the
   * log looking for a restore that never happened. */
  logPrefix?: string;
}

/** How long a restore waits for the server-database handle to be attached before giving up.
 *
 * After a warm resume the restore page is on screen within about 50ms, but the handle only arrives
 * once the background couch login has finished (design-docs/warm-resume.md, Phase B). On a slow
 * network that login can take several seconds — nightly run 37557086506 saw its first attempt time
 * out after 5s, and the handle arrived 7s after the page did. A restore tapped in that gap used to
 * fail at once with nothing written. Bounded, so a device that is genuinely offline still gets the
 * failure alert rather than a spinner that never ends. */
export const RESTORE_REMOTE_ATTACH_WAIT_MS = 30000;

/** How often the wait above checks whether the handle has arrived. */
export const RESTORE_REMOTE_ATTACH_POLL_MS = 250;

export enum RemoteAttachWaitOutcome {
  /** the handle was already there, so nothing waited */
  AlreadyAttached = 'already-attached',
  /** the handle arrived during the wait */
  AttachedAfterWait = 'attached-after-wait',
  /** the time ran out and the handle never arrived */
  TimedOut = 'timed-out',
  /** the caller said to stop waiting, because the handle can no longer arrive */
  GaveUp = 'gave-up',
}

export interface WaitForRemoteAttachOptions {
  /** true once the server-database handle is attached */
  isAttached: () => boolean;
  /** true when the handle can never arrive any more, for example because the database is shutting down */
  shouldGiveUp: () => boolean;
  timeoutMs: number;
  pollMs: number;
  sleepAsync: (ms: number) => Promise<void>;
  /** milliseconds from any fixed point; injected so a test can drive the clock */
  nowMs: () => number;
}

/** Wait, up to `timeoutMs`, for the server-database handle to be attached. It does not throw: the
 * caller keeps its own "remote not available" error, so a handle that never comes fails exactly as
 * it did before this wait existed. Polled rather than awaited on a promise because several paths set
 * the handle back to null (a purge, a shutdown, the restore itself), and a promise would need
 * re-arming on each of them. */
export async function waitForRemoteAttachAsync(options: WaitForRemoteAttachOptions): Promise<RemoteAttachWaitOutcome> {
  const { isAttached, shouldGiveUp, timeoutMs, pollMs, sleepAsync, nowMs } = options;
  if (isAttached()) return RemoteAttachWaitOutcome.AlreadyAttached;

  const deadline = nowMs() + timeoutMs;
  // while the handle is missing and there is time left, check again after a short pause
  while (nowMs() < deadline) {
    if (shouldGiveUp()) return RemoteAttachWaitOutcome.GaveUp;
    await sleepAsync(pollMs);
    if (isAttached()) return RemoteAttachWaitOutcome.AttachedAfterWait;
  }
  return RemoteAttachWaitOutcome.TimedOut;
}

/** Linear backoff: 500ms before the 2nd attempt, 1000ms before the 3rd, and so on. */
export function retryDelayMs(attempt: number): number {
  return 500 * attempt;
}

export async function retryOnTransientErrorAsync<T>(
  call: () => Promise<T>,
  options: RetryOnTransientErrorOptions,
): Promise<T> {
  const { operationLabel, maxAttempts, isTransient, sleepAsync, onRetry, logPrefix = '[restore]' } = options;

  // for each attempt, until the call succeeds or we run out of attempts
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await call();
    } catch (err) {
      // if this is a real fault, or we have no attempts left, give up and let the caller see it
      const isLastAttempt = attempt >= maxAttempts;
      if (!isTransient(err) || isLastAttempt) {
        console.error(`${logPrefix} ${operationLabel} failed after ${attempt} attempt(s)`, err);
        throw err;
      }
      const delayMs = retryDelayMs(attempt);
      console.log(`${logPrefix} ${operationLabel}: transient network error on attempt ${attempt}/${maxAttempts}, retrying in ${delayMs}ms`);
      onRetry?.();
      await sleepAsync(delayMs);
    }
  }

  // unreachable: the loop either returns the result or throws on its last attempt. present so the
  // function is total, and so a future edit to the loop bound cannot silently fall through to
  // `undefined` (which a caller would read as a successful restore).
  throw new Error(`${logPrefix} ${operationLabel}: retry loop exhausted without a result`);
}

/** Check a `bulkDocs` chunk result, and throw if any document actually failed to be written.
 *
 * `bulkDocs` does NOT throw when individual documents fail — it resolves with a per-row `{ id, error }`.
 * So a restore that ignored the result entirely (as this one used to) could report success having
 * silently written nothing, and the caller would reload into a half-restored database with no clue why.
 *
 * WHY A `conflict` ROW IS NOT TREATED AS A FAILURE HERE. It is tempting to say "a conflict on a first
 * attempt means that document was not written, so throw". That is wrong for this restore, and enforcing it
 * broke a perfectly healthy restore in E2E — which is how both of the known cases below were found. Real
 * restores produce first-attempt conflicts EVERY time and complete correctly anyway:
 *
 * - `_design/*` — BY DESIGN. The restore deliberately PRESERVES the remote's design documents (they are
 *   filtered out of the delete pass), so a fixture — being a full database dump — carries its own
 *   `_design/view` and is expected to lose that write to the live one.
 * - `settings` — observed on every fixture restore. Not fully explained (most likely the singleton being
 *   re-created on the remote between the delete pass and the upload). It is PRE-EXISTING behaviour that the
 *   restore has always tolerated, and changing it is a data-model decision, not something a CI fix should
 *   smuggle in.
 *
 * So conflicts are tolerated — but never silently. They are logged, and their ids are returned so the
 * caller can surface an UNEXPECTED one. Every other per-doc error is a genuine failure to write, and throws.
 *
 * Returns the ids of the tolerated conflict rows. */
export function assertBulkDocsChunkSucceeded(result: unknown, operationLabel: string): string[] {
  // do NOT fail open on an unexpected shape: treating a non-array as "all good" would blind the one
  // check that exists to catch a silently-unwritten chunk.
  if (!Array.isArray(result)) {
    throw new Error(`[restore] ${operationLabel}: bulkDocs returned an unexpected result (${typeof result})`);
  }

  const rows = result as Array<{ id?: string; error?: string }>;
  const failures = rows.filter(row => !!row.error);
  if (failures.length === 0) {
    return [];
  }

  // NOT the shared `isPouchConflict` on purpose: these are raw `bulkDocs` rows as CouchDB
  // returned them over HTTP, where the word sits in `error` — the shared predicate reads
  // `status` and `name`, so it would answer false for every row here.
  const conflicts = failures.filter(row => row.error === 'conflict');
  const realFailures = failures.filter(row => row.error !== 'conflict');

  if (conflicts.length > 0) {
    // tolerated, but never silent — this is the breadcrumb a future debugger needs
    console.log(`[restore] ${operationLabel}: ${conflicts.length} conflict row(s), tolerated: ${conflicts.map(row => row.id).join(', ')}`);
  }

  if (realFailures.length > 0) {
    const summary = realFailures.slice(0, 5).map(row => `${row.id}: ${row.error}`).join(', ');
    const message = `[restore] ${operationLabel}: ${realFailures.length} of ${rows.length} docs failed (${summary})`;
    // terminal failure — log it before throwing, so the reason lands in the browser console (the artifact
    // this class of bug is actually diagnosed from) and not only inside a telemetry payload
    console.error(message, realFailures.slice(0, 20));
    throw new Error(message);
  }

  return conflicts.map(row => row.id ?? '<unknown>');
}

/** The conflicts a restore is EXPECTED to produce (see `assertBulkDocsChunkSucceeded`). Any other
 * conflicting id means a document the restore did not write, and is worth reporting. */
export function isExpectedRestoreConflictId(id: string): boolean {
  return id.startsWith('_design/') || id === 'settings';
}

/** True for a document the restore deliberately leaves on the server rather than replacing: our design
 * documents and the user's snapshots. The delete pass skips them, so the check after the upload must not
 * treat one as a leftover either. */
export function isPreservedByRestoreId(id: string): boolean {
  return id.startsWith('snapshot/') || id.startsWith('_design/');
}

/** How many times the restore may list the server after its upload, to check what really ended up there.
 *
 * The last listing gets no repair after it — it is the verdict — so this allows two repair rounds. Bounded
 * on purpose: a server that keeps handing foreign documents back must be reported, not spun on. */
export const RESTORE_MAX_VERIFY_LISTINGS = 3;

/** the subset of an `allDocs({ include_docs: true, conflicts: true })` row the post-upload check reads */
export interface RestoreVerifyRow {
  id: string;
  value?: { rev?: string };
  doc?: { _conflicts?: string[] };
}

export interface RestoreRepairPlan {
  /** on the server, but not part of what was restored — delete them */
  strayIds: string[];
  /** part of what was restored, but the server's copy is not (only) ours — delete every branch, then
   * upload the restored document again */
  replacedIds: string[];
  /** the delete rows for both lists: each document's winning revision and each losing branch */
  deletes: Array<{ _id: string; _rev: string; _deleted: true }>;
}

/** Work out what the server still holds that a restore did not put there, from a listing taken after the
 * upload. Why a restore needs this — a cancelled sync's last push landing after the restore listed the
 * server — and the gap it accepts are in design-docs/boot-sequence.md.
 *
 * - an id that was not uploaded, and is not preserved by the restore, is a STRAY;
 * - an uploaded id whose upload conflicted (`conflictedIds`), or whose server copy now carries losing
 *   branches, is REPLACED — its current content is not reliably the restored document;
 * - an id in `conflictedIds` that the listing does not show at all is REPLACED too: it is simply missing.
 *
 * `settings` and `_design/*` are left alone exactly as `isExpectedRestoreConflictId` already leaves them. */
export function planRestoreRepairPass(
  rows: RestoreVerifyRow[],
  uploadedIds: ReadonlySet<string>,
  conflictedIds: ReadonlySet<string>,
): RestoreRepairPlan {
  const strayIds: string[] = [];
  const replacedIds: string[] = [];
  const deletes: RestoreRepairPlan['deletes'] = [];
  const listedIds = new Set<string>();

  // for each document currently on the server
  for (const row of rows) {
    listedIds.add(row.id);
    if (isPreservedByRestoreId(row.id) || isExpectedRestoreConflictId(row.id)) continue;

    const losingRevs = row.doc?._conflicts ?? [];
    if (!uploadedIds.has(row.id)) {
      strayIds.push(row.id);
    } else if (conflictedIds.has(row.id) || losingRevs.length > 0) {
      replacedIds.push(row.id);
    } else {
      continue;
    }

    // close every branch, or a losing one becomes the winner the moment the winner is deleted
    if (row.value?.rev) {
      deletes.push({ _id: row.id, _rev: row.value.rev, _deleted: true });
    }
    for (const rev of losingRevs) {
      deletes.push({ _id: row.id, _rev: rev, _deleted: true });
    }
  }

  // for each upload that conflicted but is no longer on the server, the restored document is missing
  for (const id of conflictedIds) {
    if (listedIds.has(id) || !uploadedIds.has(id)) continue;
    if (isPreservedByRestoreId(id) || isExpectedRestoreConflictId(id)) continue;
    replacedIds.push(id);
  }

  return { strayIds, replacedIds, deletes };
}
